/**
 * ThemeProvider — fetches available themes once, applies the active theme's
 * CSS variables + font stacks to <html>.
 *
 * Theme tokens map to the Tailwind/shadcn HSL variables in index.css.
 *
 * NOTE on localStorage: we persist ONLY a non-sensitive theme code so the user's
 * preference survives a refresh before the auth call resolves. Wrapped in
 * try/catch to handle private/incognito browsers where localStorage throws.
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const ThemeContext = createContext({
    themes: [],
    activeCode: null,
    setTheme: () => {},
    refreshThemes: () => {},
});

const STORAGE_KEY = "ff-theme-code";

// Safe localStorage helpers — never throw in private-mode browsers.
const readStored = () => {
    try { return localStorage.getItem(STORAGE_KEY); }
    catch (e) { console.warn("[theme] localStorage read blocked:", e?.message); return null; }
};
const writeStored = (value) => {
    try { value ? localStorage.setItem(STORAGE_KEY, value) : localStorage.removeItem(STORAGE_KEY); }
    catch (e) { console.warn("[theme] localStorage write blocked:", e?.message); }
};

const TOKEN_KEYS = [
    "background", "foreground",
    "primary", "primary-foreground",
    "secondary", "muted-foreground",
    "card", "border",
    "accent", "destructive",
];

function applyToDocument(theme) {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (!theme) {
        TOKEN_KEYS.forEach((k) => root.style.removeProperty(`--${k}`));
        root.style.removeProperty("--font-body");
        root.style.removeProperty("--font-display");
        document.body && (document.body.style.fontFamily = "");
        return;
    }
    const tokens = theme.tokens || {};
    TOKEN_KEYS.forEach((k) => {
        if (tokens[k]) root.style.setProperty(`--${k}`, tokens[k]);
        else root.style.removeProperty(`--${k}`);
    });
    const fonts = theme.fonts || {};
    if (fonts.body) {
        root.style.setProperty("--font-body", fonts.body);
        document.body && (document.body.style.fontFamily = fonts.body);
    }
    if (fonts.display) root.style.setProperty("--font-display", fonts.display);
}

export function ThemeProvider({ children }) {
    const { user } = useAuth();
    const [themes, setThemes] = useState([]);
    const [orgDefault, setOrgDefault] = useState(null);
    const [activeCode, setActiveCode] = useState(() => readStored());

    const refreshThemes = useCallback(async () => {
        if (!user) return;
        try {
            const [{ data }, def] = await Promise.all([
                api.get("/themes"),
                api.get("/themes/org-default").catch(() => ({ data: { default_theme_code: null } })),
            ]);
            setThemes(data);
            setOrgDefault(def.data?.default_theme_code || null);
        } catch (err) {
            // Anon / new orgs have no themes yet — log but don't break the app
            console.warn("[theme] /themes fetch failed:", err?.response?.status || err?.message);
        }
    }, [user]);

    useEffect(() => { refreshThemes(); }, [refreshThemes]);

    // Sync active code from logged-in user, falling back to the org default
    // when the user hasn't picked their own.
    useEffect(() => {
        if (!user) { setActiveCode(null); return; }
        if (user.theme_code) {
            setActiveCode(user.theme_code);
            writeStored(user.theme_code);
        } else if (orgDefault) {
            setActiveCode(orgDefault);
            // Do NOT write to localStorage — user's preference stays "inherit".
        }
    }, [user, orgDefault]);

    // Apply CSS vars whenever theme list/active code changes
    useEffect(() => {
        const active = themes.find((t) => t.code === activeCode);
        applyToDocument(active);
    }, [themes, activeCode]);

    const setTheme = useCallback(async (code) => {
        setActiveCode(code);
        writeStored(code);
        try { await api.put("/themes/me", { theme_code: code }); }
        catch (err) { console.warn("[theme] persist failed:", err?.response?.status || err?.message); }
    }, []);

    const setOrgDefaultTheme = useCallback(async (code) => {
        try {
            await api.put("/themes/org-default", { theme_code: code });
            setOrgDefault(code);
        } catch (err) { throw err; }
    }, []);

    const value = useMemo(() => ({ themes, activeCode, setTheme, refreshThemes, orgDefault, setOrgDefaultTheme }), [themes, activeCode, setTheme, refreshThemes, orgDefault, setOrgDefaultTheme]);
    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
